/**
 * Analytics event port.
 *
 * All product analytics flow through `sendEvent`, which forwards to the
 * native `_PuerInterface.sendEvent(name, jsonPayload?)`. Payloads are
 * JSON-stringified; if the stringified call fails we retry name-only so we
 * never lose the bare signal.
 */
import { getInterface } from './puerInterface.js';
import { getItem, setItem } from '../lib/storage.js';

const VISIT_SENT_KEY = '_visit_xsoda_sent';

/** Event names, centralised so call-sites can't typo them. */
export const Events = Object.freeze({
  Visit: 'visit_xsoda',
  VideoWatched: 'video_watched',
  VideoReaction: 'video_reaction',
  VideoFavorite: 'video_favorite', // NEW: favorites feature
  VideoError: 'video_error',
});

/**
 * @param {string} name
 * @param {Record<string, unknown>=} payload
 */
export function sendEvent(name, payload) {
  const iface = getInterface();
  if (!iface || typeof iface.sendEvent !== 'function') {
    // In a plain browser we just log so the flow is observable while developing.
    if (import.meta.env?.DEV) console.debug('[event]', name, payload ?? '');
    return;
  }
  try {
    payload ? iface.sendEvent(name, JSON.stringify(payload)) : iface.sendEvent(name);
  } catch {
    if (payload) {
      try {
        iface.sendEvent(name);
      } catch {
        /* noop */
      }
    }
  }
}

/** Fire the one-time `visit_xsoda` event (idempotent via localStorage). */
export function sendVisitOnce() {
  if (getItem(VISIT_SENT_KEY)) return;
  const iface = getInterface();
  if (!iface || typeof iface.sendEvent !== 'function') return;
  try {
    iface.sendEvent(Events.Visit);
    setItem(VISIT_SENT_KEY, '1');
  } catch {
    /* noop */
  }
}
